import React, { lazy, Suspense } from 'react';
import { Route, Navigate } from 'react-router-dom';
import LoadingFallback from './LoadingFallback';

const WholesaleOverviewAnalytics = lazy(() => import('../components/wholesale/overview/analytics'));
const WholesaleCustomers = lazy(() => import('../components/wholesale/customers'));
const WholesaleCustomerDetailPage = lazy(() => import('../components/wholesale/customers/CustomerDetailPage'));
const WholesaleCustomerAmazonPage = lazy(() => import('../components/wholesale/customer-amazon-sales/AmazonStorefrontPage'));
const WholesaleCatalogue = lazy(() => import('../components/wholesale/catalogue'));
const WholesaleCatalogueDownload = lazy(() => import('../components/wholesale/catalogue/download'));
const WholesaleOrders = lazy(() => import('../components/wholesale/orders'));
const WholesaleOrderCapture = lazy(() => import('../components/wholesale/orders/capture'));
const WholesaleCustomerDetailRoute = lazy(() => import('../components/wholesale/orders/components/CustomerDetailPanel'));
const RedirectOrdersCustomerHub = lazy(() => import('../components/wholesale/customers/RedirectOrdersCustomerHub'));
const RedirectOrdersCustomerDeliveryInvoice = lazy(() => import('../components/wholesale/customers/RedirectOrdersCustomerDeliveryInvoice'));
const CutDeliveryInvoice = lazy(() => import('../components/wholesale/orders/cut-delivery-invoice'));
const WholesaleOperations = lazy(() => import('../components/wholesale/operations'));
const WholesaleRouteRedirect = lazy(() =>
  import('../components/wholesale/operations/WholesaleRouteRedirect').then((m) => ({
    default: m.WholesaleRouteRedirect,
  }))
);
const WholesaleSourcing = lazy(() => import('../components/wholesale/sourcing'));
const WholesaleCockpit = lazy(() => import('../components/wholesale/cockpit'));
const CockpitProposals = lazy(() => import('../components/wholesale/cockpit/ProposalsInbox'));
const PricingAdmin = lazy(() => import('../components/wholesale/cockpit/pricing-admin'));
const WholesaleProfitability = lazy(() => import('../components/wholesale/profitability'));
const WholesaleShell = lazy(() => import('../components/shared/section-shells/WholesaleShell'));
const WholesalePageWrapper = lazy(() => import('../components/wholesale/WholesalePageWrapper'));
const UngatingInvoices = lazy(() => import('../components/wholesale/ungating-invoices'));
const WholesalePostConversion = lazy(() => import('../components/wholesale/post-conversion'));
const WholesaleCustomerDetail = lazy(() => import('../components/wholesale/post-conversion/CustomerDetail'));
const WholesaleRiskQueue = lazy(() => import('../components/wholesale/post-conversion/RiskQueue'));
const WholesaleFunnelView = lazy(() => import('../components/wholesale/post-conversion/FunnelView'));
const WholesaleMatchReview = lazy(() => import('../components/wholesale/post-conversion/MatchReview'));
const CustomerSalesTracker = lazy(() => import('../components/wholesale/customer-sales-tracker'));
const WholesaleShipments = lazy(() => import('../components/wholesale/shipments'));
const WholesaleFulfillmentQueue = lazy(() => import('../components/wholesale/fulfillment/FulfillmentQueue'));
const WholesaleRepricing = lazy(() => import('../components/wholesale/repricing/RepricingRoutes'));
const WholesaleAdvisor = lazy(() => import('../components/wholesale/advisor'));
const WholesaleExclusivity = lazy(() => import('../components/wholesale/exclusivity'));
const ExternalApiDocs = lazy(() => import('../components/wholesale/api-docs/ExternalApiDocs'));
const OPLOGInvoiceDrop = lazy(() => import('../components/other/oplog-invoices'));

export const wholesaleRoutes = (
  <>
    <Route path="/wholesale/operations" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleOperations /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/overview" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleRouteRedirect tab="metrics" />
      </Suspense>
    } />
    <Route path="/wholesale/overview/analytics" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleOverviewAnalytics /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/today" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleRouteRedirect tab="queue" />
      </Suspense>
    } />
    <Route path="/wholesale/sourcing" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleSourcing /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/cockpit" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide>
          <WholesaleCockpit />
        </WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/cockpit/proposals" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><CockpitProposals /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/cockpit/pricing-admin/*" element={
      <Suspense fallback={<LoadingFallback />}>
        <PricingAdmin />
      </Suspense>
    } />
    <Route path="/wholesale/profitability" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleProfitability /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/customers/new" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleCustomerDetailPage mode="create" /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/customers/:customerRef/edit" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleCustomerDetailPage mode="edit" /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/customers/:customerRef/amazon" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleCustomerAmazonPage /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/customers/:customerRef/workstation" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesalePageWrapper noHeader wide><WholesaleCustomerDetailRoute /></WholesalePageWrapper>
      </Suspense>
    } />
    <Route path="/wholesale/customers/:customerRef/delivery-invoice" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesalePageWrapper noHeader wide><CutDeliveryInvoice /></WholesalePageWrapper>
      </Suspense>
    } />
    <Route path="/wholesale/customers/:customerRef" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleCustomerDetailPage /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/customers" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleCustomers /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/customer-sales-tracker" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><CustomerSalesTracker /></WholesaleShell>
      </Suspense>
    } />
    {/* Single unified catalogue page. Legacy paths redirect with the
        region pre-selected as a filter on the consolidated view. */}
    <Route path="/wholesale/catalogue" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleCatalogue /></WholesaleShell>
      </Suspense>
    } />
    {/* Download Catalogue — the old dialog promoted to a full page,
        designed like the orders pages (own DetailHeader, so noHeader). */}
    <Route path="/wholesale/catalogue/download" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesalePageWrapper noHeader wide><WholesaleCatalogueDownload /></WholesalePageWrapper>
      </Suspense>
    } />
    <Route path="/wholesale/catalogue/uk" element={<Navigate to="/wholesale/catalogue?region=UK" replace />} />
    <Route path="/wholesale/catalogue/us" element={<Navigate to="/wholesale/catalogue?region=US" replace />} />
    <Route path="/wholesale/catalogue/uk-long" element={<Navigate to="/wholesale/catalogue?region=UK" replace />} />
    <Route path="/wholesale/catalogue/us-long" element={<Navigate to="/wholesale/catalogue?region=US" replace />} />
    {/* Distribution page retired: AI capture moved to /wholesale/orders
        (header button), Send Catalogue moved to /wholesale/catalogue. */}
    <Route path="/wholesale/distribution" element={<Navigate to="/wholesale/orders" replace />} />
    <Route path="/wholesale/orders" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesalePageWrapper noHeader wide><WholesaleOrders /></WholesalePageWrapper>
      </Suspense>
    } />
    <Route path="/wholesale/orders/capture" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesalePageWrapper noHeader wide><WholesaleOrderCapture /></WholesalePageWrapper>
      </Suspense>
    } />
    <Route path="/wholesale/order/capture" element={<Navigate to="/wholesale/orders/capture" replace />} />
    <Route path="/wholesale/orders/customers/:customerRef/delivery-invoice" element={
      <Suspense fallback={<LoadingFallback />}>
        <RedirectOrdersCustomerDeliveryInvoice />
      </Suspense>
    } />
    <Route path="/wholesale/orders/customers/:customerRef" element={
      <Suspense fallback={<LoadingFallback />}>
        <RedirectOrdersCustomerHub />
      </Suspense>
    } />
    <Route path="/wholesale/orders/:orderId" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesalePageWrapper noHeader wide><WholesaleOrders /></WholesalePageWrapper>
      </Suspense>
    } />
    <Route path="/wholesale/shipments" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShipments />
      </Suspense>
    } />
    <Route path="/wholesale/shipments/:shipmentRef" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShipments />
      </Suspense>
    } />
    <Route path="/wholesale/fulfillment" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesalePageWrapper wide><WholesaleFulfillmentQueue /></WholesalePageWrapper>
      </Suspense>
    } />
    <Route path="/wholesale/kalonomia-invoices" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><UngatingInvoices /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/exclusivity" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleExclusivity /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/post-conversion" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesalePostConversion /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/post-conversion/customers/:customerId" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleCustomerDetail /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/post-conversion/risk" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleRiskQueue /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/post-conversion/funnel" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleFunnelView /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/post-conversion/match-review" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide><WholesaleMatchReview /></WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/3pl-invoices" element={<OPLOGInvoiceDrop />} />
    <Route path="/api-docs" element={
      <Suspense fallback={<LoadingFallback />}>
        <ExternalApiDocs />
      </Suspense>
    } />
    <Route path="/wholesale/repricing/*" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleShell wide>
          <WholesaleRepricing />
        </WholesaleShell>
      </Suspense>
    } />
    <Route path="/wholesale/advisor" element={
      <Suspense fallback={<LoadingFallback />}>
        <WholesaleAdvisor />
      </Suspense>
    } />
  </>
);
